import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { EntitlementService } from '@core/services/entitlement.service';
import type { FeatureKey } from '@core/models/platform.model';

/**
 * Restrict a route to schools whose plan (or add-ons) include a module.
 * Configure via route data, alongside roleGuard:
 *   { path: 'admin/fees/collect', canActivate: [roleGuard, featureGuard],
 *     data: { roles: [...], feature: 'fees' } }
 * `feature` may also be a list — then EVERY listed module is required (e.g.
 * exam results read the report-card endpoints: ['exams', 'reportCards']).
 * Waits for the school's entitlements (starting the load if needed), then
 * allows or redirects to /upgrade. The API enforces the same rule.
 */
export const featureGuard: CanActivateFn = async (route) => {
  const auth = inject(AuthService);
  const entitlements = inject(EntitlementService);
  const router = inject(Router);

  const data = route.data['feature'] as FeatureKey | FeatureKey[] | undefined;
  const required = data === undefined ? [] : Array.isArray(data) ? data : [data];
  const allowed = (): boolean => required.every((f) => entitlements.has(f));
  if (allowed()) {
    return true;
  }
  if (!auth.isLoggedIn()) {
    return router.createUrlTree(['/login']);
  }
  await entitlements.ensureLoaded();
  return allowed() ? true : router.createUrlTree(['/upgrade']);
};
